import { Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

import { StylistService } from '../../../services/stylist.service';
import { UserService } from '../../../services/user.service';
import { Utilities } from '../../../utilities/utilities';
import { Tax } from '../../../models/tax/tax.model';
import { Lineitem } from '../../../models/line-item/line-item.model';
import { Stylist } from '../../../models/stylist/stylist.model';
import { Sale } from '../../../models/sale/sale.model';
import { List } from 'immutable';
import { SaleDiscountListModalCallBackData } from '../sale-discount-list/sale-discount-list.page';
import { SaleDiscountListPage } from '../sale-discount-list/sale-discount-list.page';
import { MaskitoOptions, MaskitoElementPredicateAsync } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';

export interface LineitemConfiguratorModalCallBackData {
  lineitem: Lineitem,
  action: string
}

@Component({
  selector: 'app-lineitem-configurator',
  templateUrl: './lineitem-configurator.page.html',
  styleUrls: ['./lineitem-configurator.page.scss'],
})
export class LineitemConfiguratorPage implements OnInit {

  public isLoading: boolean;

  public errorOccurredWhileLoadingProducts: boolean;

  public lineitem: Lineitem;

  @Input() sale: Sale;

  @Input() lineItemForm: UntypedFormGroup;

  public quantity: UntypedFormControl;

  public price: UntypedFormControl;

  public taxOptions: Array<{ tax: Tax, formControlName: string, formControl: UntypedFormControl }>;

  public showRemoveButton: boolean;

  currency: string;
  currencyMask: MaskitoOptions;

  readonly maskPredicate: MaskitoElementPredicateAsync = async (el) =>
    (el as HTMLIonInputElement).getInputElement();

  constructor (
              //private navParams: NavParams,
              private modalController: ModalController,
              private changeDetectorRef: ChangeDetectorRef,
              private formBuilder: UntypedFormBuilder,
              private userService: UserService,
              private stylistService: StylistService) {
  }

  public ngOnInit () {
    this.quantity = new UntypedFormControl(this.lineitem.quantity, Validators.compose([Validators.required]));
    this.price = new UntypedFormControl(this.lineitem.price, Validators.compose([Validators.required]));
    this.lineItemForm = this.formBuilder.group({
      quantity: this.quantity,
      price: this.price
    })

    this.taxOptions = [];
    this.sale.taxes.forEach((tax) => {
      let selected = false;
      if (this.lineitem.taxRateType === 0) {

        // default tax rate type
        if (tax.appliesTo === 2) {
          selected = true;
        } else {
          if (tax.appliesTo === 0 && this.lineitem.type === 'service') {
            selected = true;
          } else if (tax.appliesTo === 1 && this.lineitem.type === 'product') {
            selected = true;
          }
        }

      } else if (this.lineitem.taxRateType === 2) {

        // custom tax rate type
        if (this.lineitem.customTaxes) {
          this.lineitem.customTaxes.some((customTaxName: string) => {

            if (tax.name === customTaxName) {
              selected = true;
              return true;
            }

            return false;
          });
        }

      }

      const settings = {
        tax: tax,
        formControlName: tax.name,
        formControl: new UntypedFormControl(selected)
      };
      this.taxOptions.push(settings);
      this.lineItemForm.addControl(settings.formControlName, settings.formControl);
    });

    this.showRemoveButton = this.sale.findLineItemIndex(this.lineitem) > -1 ? true : false;

    this.stylistService.getDiscounts(this.lineitem.stylist).subscribe((discounts) => {
    });

    this.currency = Utilities.decodeHTML(
      this.userService.getSalon().getCurrency()
    );

    this.currencyMask = maskitoNumberOptionsGenerator({
      decimalZeroPadding: true,
      precision: 2,
      decimalSeparator: '.',
      thousandSeparator: ',',
      min: 0,
      prefix: this.currency,
    });
  }

  public onDismiss () {
    //this.viewController.dismiss();  
    this.modalController.dismiss();     
  }

  public onTaxToggle () {
    // set to custom tax
    this.lineitem = this.lineitem.setTaxRateType(2);
  }

  public removeItemClickHandler () {
    const data: LineitemConfiguratorModalCallBackData = {
      lineitem: this.lineitem,
      action: 'delete'
    }
    //this.viewController.dismiss(data);
    this.modalController.dismiss(data);
  }

  public save () {
    let lineitem: Lineitem = this.lineitem;
    lineitem = lineitem.setPrice(Utilities.extractNumberFromString(this.price.value));
    lineitem = lineitem.setQuantity(parseInt(this.quantity.value, 10));

    let customTaxes: List<string> = List([]);
    if (lineitem.taxRateType === 2) {
      for (let option of this.taxOptions) {
    
        if (option.formControl.value) {
          customTaxes = customTaxes.push(option.tax.name); 
        }
      }
    }

    lineitem = lineitem.setCustomTaxes(customTaxes);

    const data: LineitemConfiguratorModalCallBackData = {
      lineitem: lineitem,
      action: 'save'
    }
    //this.viewController.dismiss(data);
    this.modalController.dismiss(data);
  }

  public async viewDiscountPicker ($event) {
    $event.stopPropagation();
    const modal = await this.modalController.create({
      component: SaleDiscountListPage,
      componentProps: { lineitem: this.lineitem }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.lineitem = this.lineitem.setDiscount(data.data.discount);
        this.changeDetectorRef.markForCheck();
      }
    });
    await modal.present();
  }
  
  // public slideRemove (itemSlide: ItemSliding, item: Item, $event) {

  //  $event.stopPropagation();
  
  //  // src: http://blog.ihsanberahim.com/2017/05/trigger-ionitemsliding-using-click-event.html  
  //  let swipeAmount = 100; //set your required swipe amount

  //  itemSlide.startSliding(swipeAmount);
  //  itemSlide.moveSliding(swipeAmount);

  //  itemSlide.setElementClass('active-options-right', true);
  //  itemSlide.setElementClass('active-swipe-right', true);

  //  item.setElementStyle('transition', null);
  //  item.setElementStyle('transform', 'translate3d(-'+swipeAmount+'px, 0px, 0px)');
  // }

  public removeDiscount ($event) {
    $event.stopPropagation();
    this.lineitem = this.lineitem.removeDiscount();
  }

  public onStylistSelect (stylist: Stylist) {
    this.lineitem = this.lineitem.setStylist(stylist);
  }
  

}
